import { useEffect, useRef, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Asking before acting, anywhere in the product.
 *
 * ⚠️ These lived in `admin/shared.tsx`, and the move out of it is the point rather than
 * tidying. The rule they encode — nothing destructive happens on one click, including things
 * that are reversible, because "reversible" is no comfort to the people affected in between —
 * was written for administration and stopped at its boundary. Everywhere else in the product
 * a destructive control fired immediately: a dropdown that closed fifty requests, an X that
 * revoked a colleague's access to a PHI-bearing record, an icon that deleted a private note.
 *
 * A rule that only applies where it happens to be imported from is not a rule. It lives in a
 * neutral place now so that reaching for it is the obvious thing to do.
 */

/**
 * A control that asks before it acts.
 *
 * ⚠️ EVERY destructive or disabling action goes through this or its panel below, and the
 * breadth is the point. Deleting was already confirmed in one or two places and switching
 * something OFF was not, on the reasoning that closing a site or a team is reversible with
 * the button beside it. That reasoning does not survive contact with a live system: closing
 * a site removes it from every picker immediately, turning a feature flag off can stop the
 * service desk notifying anybody, and "reversible" is no comfort to the people affected in
 * between. A single misplaced click should not be able to do any of it.
 *
 * Deliberately NOT a modal. It expands in place, so the row it belongs to stays on screen
 * and the question names the thing being acted on rather than appearing over the top of it.
 *
 * Nothing here is a security control — every one of these actions is re-checked server-side
 * against a permission, and a caller that skips the UI skips this with it. It exists to stop
 * an authorised person doing an authorised thing by accident.
 */
export function ConfirmButton({
  label,
  question,
  detail,
  confirmLabel,
  onConfirm,
  disabled,
  tone = "destructive",
  icon,
  className,
}: {
  /** The resting label, e.g. "Delete". */
  label: React.ReactNode;

  /** Names the thing, e.g. "Delete (PLR) Palmer Lake Recovery permanently?" */
  question: string;

  /** What it will do, and what it will not. Optional but usually worth saying. */
  detail?: string;

  /** The label on the button that actually does it. Defaults to the resting label. */
  confirmLabel?: string;

  onConfirm: () => void | Promise<void>;
  disabled?: boolean;

  /** `destructive` for anything unrecoverable; `warning` for switching something off. */
  tone?: "destructive" | "warning";

  icon?: React.ReactNode;
  className?: string;
}) {
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const cancelRef = useRef<HTMLButtonElement | null>(null);

  // Focus lands on Cancel, never on the button that does the thing: a stray Enter after the
  // control opens must not complete the action it just asked about.
  useEffect(() => {
    if (asking) cancelRef.current?.focus();
  }, [asking]);

  if (!asking) {
    return (
      <button
        type="button"
        onClick={() => setAsking(true)}
        disabled={disabled}
        className={cn(
          "inline-flex h-8 items-center gap-1.5 rounded-lg border border-input px-2.5 text-xs transition hover:bg-accent disabled:opacity-50",
          tone === "destructive" && "text-destructive hover:bg-destructive/10",
          className,
        )}
      >
        {icon}
        {label}
      </button>
    );
  }

  return (
    <ConfirmPanel
      question={question}
      detail={detail}
      confirmLabel={confirmLabel ?? (typeof label === "string" ? label : "Confirm")}
      tone={tone}
      busy={busy}
      cancelRef={cancelRef}
      onCancel={() => setAsking(false)}
      onConfirm={async () => {
        setBusy(true);

        try {
          await onConfirm();
          setAsking(false);
        } finally {
          // Reopened rather than left spinning if the server refused: the error is rendered
          // by the caller, and a control stuck in "Working…" hides it.
          setBusy(false);
        }
      }}
    />
  );
}

/**
 * The question itself, without a trigger.
 *
 * ⚠️ Separate from <see cref="ConfirmButton"/> because half the destructive controls in
 * administration are icons in a tight row, and expanding one in place would collapse the row
 * around it. Those keep their icon and render this underneath, which is also where the
 * question belongs: below the thing it is asking about, naming it.
 */
export function ConfirmPanel({
  question,
  detail,
  confirmLabel,
  tone = "destructive",
  busy = false,
  cancelRef,
  onConfirm,
  onCancel,
}: {
  question: string;
  detail?: string;
  confirmLabel: string;
  tone?: "destructive" | "warning";
  busy?: boolean;
  cancelRef?: React.Ref<HTMLButtonElement>;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
}) {
  const fallbackCancel = useRef<HTMLButtonElement | null>(null);

  // Focus lands on Cancel, never on the button that acts: a stray Enter after this appears
  // must not complete the action it has just asked about.
  useEffect(() => {
    if (cancelRef === undefined) fallbackCancel.current?.focus();
  }, [cancelRef]);

  return (
    <div
      // Announced: something has appeared that is waiting on an answer.
      role="alertdialog"
      aria-live="polite"
      className={cn(
        "mt-3 rounded-lg border p-3",
        tone === "destructive"
          ? "border-destructive/40 bg-destructive/10"
          : "border-warning/40 bg-warning/10",
      )}
    >
      <p className="flex items-start gap-2 text-sm font-medium">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
        <span>{question}</span>
      </p>

      {detail && <p className="mt-1 pl-5 text-xs text-muted-foreground">{detail}</p>}

      <div className="mt-2 flex flex-wrap gap-2 pl-5">
        <button
          type="button"
          disabled={busy}
          onClick={() => void onConfirm()}
          className={cn(
            "inline-flex h-8 items-center rounded-lg px-3 text-xs font-medium transition hover:opacity-90 disabled:opacity-50",
            tone === "destructive"
              ? "bg-destructive text-destructive-foreground"
              : "bg-primary text-primary-foreground",
          )}
        >
          {busy ? "Working…" : confirmLabel}
        </button>

        <button
          type="button"
          ref={cancelRef ?? fallbackCancel}
          disabled={busy}
          onClick={onCancel}
          className="inline-flex h-8 items-center rounded-lg border border-input bg-background px-3 text-xs transition hover:bg-accent disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
